import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Divider, Alert, Paper, IconButton } from '@mui/material';
import { Lend } from '../../../../models/lend';
import CloseIcon from '@mui/icons-material/Close';
import { nowOperationalDateString, nowOperationalDateTimeString } from '../../../../utils/operationalDateTime';
import axios from 'axios';

interface RefinanceModalProps {
  open: boolean;
  onClose: () => void;
  client: Lend | null;
  clientId?: number;
  modalData: {
    lendId: number;
    total_value: number;
    utility: number;
    term: number;
    balance: number;
    count_refinance: number;
    start_value: number;
    start_date: string;
    user_id: string; // Puedes tiparlo mejor si quieres
  } | null;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  onPaymentSuccess: () => void;

}


// Estilos del modal
const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: '90vw', // Usa el 90% del ancho de la pantalla
  maxWidth: 500, // Pero no excedas los 500px en pantallas grandes
  height: '80vh', // Usa el 80% del alto de la pantalla
  maxHeight: 500, // Altura máxima para escritorio
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 2,
  //  p: { xs: 2, sm: 4 },   
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflowY: 'auto', // Permite desplazamiento si el contenido se desborda
};


const RefinanceModal: React.FC<RefinanceModalProps> = ({ open, onClose, client, clientId, modalData, showAlert, showLoading, hideLoading, onPaymentSuccess }) => {
  const [term, setTerm] = useState('');
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState<string | number>('');
  
  const [refinancedValue, setRefinancedValue] =  useState<string | number>('');
  const [dailyPay, setDailyPay] = useState<string | number>('');
  const [balance, setBalance] = useState<string | number>('');
  const [count_refinance, setCount_Refinance] = useState<string | number>('');
  
  const [newUtility, setNewUtility] = useState<string | number>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (modalData) {
      const refinancedValue = Math.ceil(Number(modalData.balance) + Number((modalData.balance * modalData.utility) / 100)); // si quieres porcentaje
      const newUtility = Number((modalData.balance * modalData.utility) / 100)
      const newDailyPay = Math.ceil(Number(refinancedValue / modalData.term));
      const refinanceCount = Number(modalData.count_refinance + 1)
      
      setRefinancedValue(refinancedValue);
      setTerm(modalData.term.toString());
      setBalance(modalData.balance );

      setNewUtility(newUtility);
      setDailyPay(newDailyPay);
      setCount_Refinance(refinanceCount);
      setStartDate(modalData.start_date);
      setUserId(modalData.user_id);
    }
  }, [modalData]);

  const handleAccept = async () => {
    setIsSaving(true);
    
    const currentDate = nowOperationalDateTimeString();
    //console.log('Refinance Date:', currentDate)
    const formData = {
      value: balance,
      balance: refinancedValue,
      total_value: refinancedValue,
      daily_pay_value: dailyPay,     
      will_pay: 0,
      date: nowOperationalDateString(),
      value_utility: newUtility,
      lend_id: Number(clientId),
      value_start: modalData?.start_value,
      date_refinance: currentDate,
      date_start: startDate,
      count_refinance: count_refinance,
      user_id: userId
    };
    const url = `/lends/refinance/${clientId}`;
    try {
      showLoading();
      const response = await axios.put(url, formData);
      showAlert('Se ha refinanciado el préstamo exitosamente!', 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));  
      setErrorMessage('');
      onPaymentSuccess();
      onClose(); // Cerrar el modal después de guardar
    } catch (error) {
      setErrorMessage('Error al refinanciar el préstamo. Intente más tarde.');
    } finally {
      setIsSaving(false);
      hideLoading();
    }
  };

  const handleClose = () => {
    setErrorMessage('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper sx={style}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h6">
            Refinanciar Préstamo
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: "white" }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: "auto", flexGrow: 1 }}>

          <Typography variant="body1">
            ¿Está seguro de refinanciar el préstamo #{clientId} para el cliente: {client?.client?.fullname} (Nit: {client?.client?.idnumber})?
          </Typography>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Divider sx={{ mb: 2 }} />

          {/* Valor Refinanciado */}
          <TextField
            fullWidth
            label="Valor a Refinanciar"
            type="number"
            value={modalData?.balance}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          {/* Plazo */}
          <TextField
            fullWidth
            label="Plazo"
            type="number"
            value={modalData?.term}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          {/* Utilidad */}
          <TextField
            fullWidth
            label="Utilidad"
            type="number"
            value={modalData?.utility}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          {/* Valor Cuota */}
          <TextField
            fullWidth
            label="Valor Cuota"
            type="number"
            value={dailyPay}
            InputLabelProps={{ shrink: true, }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Total a Pagar"
            type="number"
            value={refinancedValue}
            InputLabelProps={{ shrink: true, }}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* Contenedor de botones en la parte inferior */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleAccept} disabled={isSaving}>
            Guardar
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default RefinanceModal;
